<template>
  <router-link
    v-if="result.id"
    :to="route"
    :aria-label="header"
    class="quick-search-result flex flex-column"
  >
    <div
      class="quick-search-top-line flex justify-content-between align-items-center"
    >
      <div class="flex flex-gap-1 align-items-center entity-id">
        <DomainAvatar
          :domain="result.domainReference"
          :domain-branding="result.domainBranding"
          size="normal"
        />
        <component
          :is="matches('id') ? 'mark' : 'span'"
          class="font-medium text-gray-darkest"
        >
          {{ header }}
        </component>
        <OrderChip v-if="isOrder" :status="result.status as OrderStatus" />
        <ShipmentStatusChip v-else :status="result.status as ShipmentStatus" />
      </div>
      <router-link v-if="result.id" :to="route" class="details-link">
        {{ t('common.viewDetails') }}
        <i aria-hidden="true" class="pi pi-arrow-right" />
      </router-link>
    </div>

    <div
      class="quick-search-middle-line flex align-items-center text-gray-darkest"
    >
      <span class="origin">
        <component :is="matches('origin.city') ? 'mark' : 'span'">
          {{ result.origin?.city }},
        </component>
        {{ result.origin?.state }}
        <component :is="matches('origin.postalCode') ? 'mark' : 'span'">
          {{ result.origin?.postalCode }}
        </component>
      </span>
      <i aria-hidden="true" class="pi pi-arrow-right text-gray-darker" />
      <span class="destination">
        <component :is="matches('destination.city') ? 'mark' : 'span'">
          {{ result.destination?.city }},
        </component>
        {{ result.destination?.state }}
        <component :is="matches('destination.postalCode') ? 'mark' : 'span'">
          {{ result.destination?.postalCode }}
        </component>
      </span>
    </div>

    <div class="quick-search-bottom-line flex flex-column align-items-start">
      <span v-if="result.firstDate" class="ship-date text-gray-darker">
        {{ t(`enumMap.DateType.${result.firstDate.type}`) }}:
        {{
          result.firstDate.dateTime
            ? d(result.firstDate.dateTime, 'numeric')
            : '-'
        }}
      </span>
      <div
        v-if="result.matchedReferenceNumbers?.length"
        class="reference-number flex"
      >
        <mark class="text-gray-darker">
          {{ result.matchedReferenceNumbers[0].typeName }}:
          {{ result.matchedReferenceNumbers[0].value }}
        </mark>
        <span class="text-gray-darker">
          {{
            result.type === 'SHIPMENT' &&
            result.matchedReferenceNumbers[0].source?.type === 'ORDER'
              ? t('common.fromParam', {
                  param: t('orders.orderNumParam', {
                    param: result.matchedReferenceNumbers[0].source.resourceId,
                  }),
                })
              : ''
          }}
        </span>
      </div>
      <span
        v-if="result.matchedTags?.length"
        class="text-gray-darker tags flex"
      >
        <mark v-for="tag in result.matchedTags" :key="tag">
          <Tag :tag="tag" readonly />
        </mark>
      </span>
    </div>
  </router-link>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { OrderStatus, QuickSearchResult, ShipmentStatus } from '@/model';

import OrderChip from '@/components/order/OrderChip.vue';
import ShipmentStatusChip from '@/components/shipment/ShipmentStatusChip.vue';
import DomainAvatar from '@rygen/rygen-ui/components/domain/DomainAvatar.vue';
import Tag from '@rygen/rygen-ui/components/tag/Tag.vue';

const props = defineProps({
  result: {
    type: QuickSearchResult,
    required: true,
  },
});
const { result } = toRefs(props);
const { d, t } = useI18n();

const isOrder = computed(() => result.value?.type === 'ORDER');
const header = computed(() => {
  return isOrder.value
    ? t('orders.orderNumParam', { param: result.value?.id })
    : t('shipments.shipmentNumParam', { param: result.value?.id });
});
const route = computed(() => {
  return isOrder.value
    ? { name: 'orderDetail', params: { orderId: result.value?.id } }
    : { name: 'shipmentDetail', params: { shipmentId: result.value?.id } };
});

const matches = (field: string) => result.value.match?.includes(field);
</script>

<style>
a.quick-search-result {
  --quick-search-background-color: initial;
  --quick-search-link-color: var(--p-primary-500);
  --quick-search-link-text-decoration: none;

  background: var(--quick-search-background-color);
  gap: var(--sp-1);
  text-decoration: none;

  &:hover {
    --quick-search-background-color: var(--p-surface-200);
    --quick-search-link-color: var(--p-primary-600);
    --quick-search-link-text-decoration: underline;
  }

  .quick-search-top-line {
    .details-link {
      display: flex;
      align-items: center;
      gap: var(--sp-1);
      color: var(--quick-search-link-color);
      text-decoration: var(--quick-search-link-text-decoration);
    }
  }

  .quick-search-middle-line {
    gap: var(--sp-1);
  }

  .quick-search-bottom-line {
    gap: var(--sp-1);

    .reference-number {
      gap: var(--sp-1);

      span {
        font-size: 0.85em;
      }
    }

    .tags {
      gap: var(--sp-1);
      flex-wrap: wrap;

      mark {
        background-color: initial;
        flex: 0 0 auto;
      }
    }
  }

  i.pi {
    font-size: 10px;
  }

  mark {
    background-color: var(--p-yellow-200);
    border-radius: var(--p-border-radius-sm);
    padding: 2px var(--sp-1);
  }
}
</style>
